import { Button, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { ChangeEvent, memo, ReactNode, useCallback, useEffect } from 'react'
import { aggregationOps, IFlow, INode } from '../../../../definitions/Nodes'
import { IAcceptedData } from '../../../../definitions/NodesDescription'
import { AddRounded, DeleteRounded, InfoRounded } from '@mui/icons-material'
import NodeManager from '../../../../managers/NodeManager'

interface P {
    acceptedData: IAcceptedData<'xfrm_aggregateBy'>
    selectedNode: INode<'xfrm_aggregateBy'>
    flow: IFlow
    setSelected: (node: INode<'xfrm_aggregateBy'>) => void
    disable: boolean
}

const NodeAggregateByEditor = ({ selectedNode, setSelected, disable, flow }: P) => {
    useEffect(() => {
        if (!selectedNode.data?.groupingData) {
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    groupingData: {
                        ...selectedNode.data.groupingData,
                        groupKeys: [],
                        aggregations: []
                    }
                }
            })
        }
    }, [])

    const handleChangeGroupKeys = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, groupKeyIdx: number) => {
        const newGroupKeys = structuredClone(selectedNode.data.groupingData.groupKeys)
        newGroupKeys[groupKeyIdx] = e.target.value
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                groupingData: {
                    ...selectedNode.data.groupingData,
                    groupKeys: newGroupKeys
                }
            }
        })
    }, [selectedNode])

    const handleRemoveGroupKeys = useCallback((groupKeyIdx: number) => {
        const newGroupKeys = structuredClone(selectedNode.data.groupingData.groupKeys)
        newGroupKeys.splice(groupKeyIdx, 1)
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                groupingData: {
                    ...selectedNode.data.groupingData,
                    groupKeys: newGroupKeys
                }
            }
        })
    }, [selectedNode])

    const handleAddGroupKeys = useCallback(() => {
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                groupingData: {
                    ...selectedNode.data.groupingData,
                    groupKeys: [...selectedNode.data.groupingData.groupKeys, '']
                }
            }
        })
    }, [selectedNode])

    const handleChangeAggregations = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, aggregationIdx: number) => {
        const { name, value } = e.target
        const newAggregations = structuredClone(selectedNode.data.groupingData.aggregations)
        newAggregations[aggregationIdx] = { ...newAggregations[aggregationIdx], [name]: value }
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                groupingData: {
                    ...selectedNode.data.groupingData,
                    aggregations: newAggregations
                }
            }
        })
    }, [selectedNode])

    const handleRemoveAggregations = useCallback((aggregationIdx: number) => {
        const newAggregations = structuredClone(selectedNode.data.groupingData.aggregations)
        newAggregations.splice(aggregationIdx, 1)
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                groupingData: {
                    ...selectedNode.data.groupingData,
                    aggregations: newAggregations
                }
            }
        })
    }, [selectedNode])

    const handleAddAggregations = useCallback(() => {
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                groupingData: {
                    ...selectedNode.data.groupingData,
                    aggregations: [...selectedNode.data.groupingData.aggregations, { field: '', op: 'count', as: '' }]
                }
            }
        })
    }, [selectedNode])

    const shape = NodeManager.getInputShapes(selectedNode, flow.nodes)[0]

    const renderInfoField = (title: string | ReactNode) => (
        <Tooltip arrow title={title} disableHoverListener={disable}>
            <InfoRounded fontSize='inherit' sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
        </Tooltip>
    )
    return (
        <Stack spacing={3}>
            <Stack spacing={2}>
                <Typography variant='h6'>Group keys</Typography>
                {selectedNode.data.groupingData?.groupKeys && selectedNode.data.groupingData.groupKeys.length > 0
                    ? (
                            <Stack spacing={1}>
                                {selectedNode.data?.groupingData?.groupKeys && selectedNode.data.groupingData.groupKeys.map((groupKey, groupKeyIdx) => (
                                    <Stack key={`groupKey-${groupKeyIdx}`} flexDirection='row' gap={1} alignItems='center'>
                                        <Stack flexDirection='row' flex={1}>
                                            <TextField
                                                size='small'
                                                fullWidth
                                                value={groupKey ?? ''}
                                                select={!!shape}
                                                onChange={e => handleChangeGroupKeys(e, groupKeyIdx)}
                                                error={groupKey === ''}
                                                disabled={disable}>
                                                {shape?.shape?.items?.properties && Object.keys(shape.shape.items.properties).map((p, i) => (
                                                    <MenuItem key={`option-${groupKeyIdx}-${i}`} value={p}>
                                                        {p}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Stack>
                                        <Stack flexDirection='row'>
                                            <IconButton
                                                size='small'
                                                onClick={() => handleRemoveGroupKeys(groupKeyIdx)}
                                                disabled={disable}>
                                                <DeleteRounded fontSize='small' color={disable ? undefined : 'error'} />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                ))}
                            </Stack>
                        )
                    : <Typography variant='caption'>Perform a group by its keys (you may choose to leave group keys empty if you want perform only aggregates operations)</Typography>}
                <Button
                    size='small'
                    startIcon={<AddRounded fontSize='small' />}
                    variant='text'
                    onClick={handleAddGroupKeys}
                    disabled={disable || (selectedNode.data?.groupingData?.groupKeys && selectedNode.data.groupingData.groupKeys.some(x => x === ''))}>
                    add key
                </Button>
            </Stack>
            <Stack spacing={2}>
                <Typography variant='h6'>Group operations</Typography>
                {selectedNode.data.groupingData?.aggregations && selectedNode.data.groupingData.aggregations.length > 0
                    ? (
                            <>
                                <Stack flexDirection='row' gap={1} alignItems='center'>
                                    <Stack flexDirection='row' gap={1} width='41%' alignItems='center'>
                                        {renderInfoField('Insert the header name of result grouping operation')}
                                        <Typography noWrap variant='subtitle2'>Alias</Typography>
                                    </Stack>
                                    <Stack flexDirection='row' sx={{ visibility: 'hidden' }}>
                                        <Typography>=</Typography>
                                    </Stack>
                                    <Stack flexDirection='row' gap={1} width='18%' alignItems='center'>
                                        {renderInfoField('Select the aggregation to perform on group')}
                                        <Typography noWrap variant='subtitle2'>
                                            Ops.
                                            <span style={{ paddingLeft: 'calc(var(--unit) / 2)', color: 'var(--color-bad)', fontWeight: 'bold' }}>*</span>
                                        </Typography>
                                    </Stack>
                                    <Stack flexDirection='row' gap={1} width='41%' alignItems='center'>
                                        {renderInfoField('Choose the input field where to perfom the aggregation (only number input are displayed)')}
                                        <Typography noWrap variant='subtitle2'>
                                            Field
                                            <span style={{ paddingLeft: 'calc(var(--unit) / 2)', color: 'var(--color-bad)', fontWeight: 'bold' }}>*</span>
                                        </Typography>
                                    </Stack>
                                    <Stack flexDirection='row'>
                                        <IconButton
                                            size='small'
                                            color='error'
                                            sx={{ visibility: 'hidden' }}>
                                            <DeleteRounded fontSize='small' />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                                <Stack spacing={1}>
                                    {selectedNode.data?.groupingData?.aggregations && selectedNode.data.groupingData.aggregations.map((aggregation, aggregationIdx) => (
                                        <Stack key={`aggregation-${aggregationIdx}`} flexDirection='row' gap={1} alignItems='center'>
                                            <Stack flexDirection='row' width='41%'>
                                                <TextField
                                                    name='as'
                                                    placeholder='field_operations'
                                                    size='small'
                                                    fullWidth
                                                    value={aggregation.as}
                                                    onChange={e => handleChangeAggregations(e, aggregationIdx)}
                                                    error={selectedNode.data.groupingData.groupKeys.includes(aggregation.as)}
                                                    disabled={disable}
                                                />
                                            </Stack>
                                            <Stack flexDirection='row'>
                                                <Typography>=</Typography>
                                            </Stack>
                                            <Stack width='18%'>
                                                <TextField
                                                    name='op'
                                                    size='small'
                                                    select
                                                    value={aggregation.op}
                                                    onChange={e => handleChangeAggregations(e, aggregationIdx)}
                                                    disabled={disable}>
                                                    {aggregationOps.map(option => (
                                                        <MenuItem key={option} value={option}>
                                                            {option}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Stack>
                                            <Stack flexDirection='row' width='41%'>
                                                <TextField
                                                    name='field'
                                                    size='small'
                                                    fullWidth
                                                    value={aggregation.op === 'count' ? '' : aggregation.field}
                                                    select={!!shape}
                                                    onChange={e => handleChangeAggregations(e, aggregationIdx)}
                                                    error={aggregation.field === ''}
                                                    disabled={disable || aggregation.op === 'count'}>
                                                    {shape?.shape && Object.entries(shape.shape.items.properties).filter(([, v]) => v.type === 'number').map(([k], i) => (
                                                        <MenuItem key={`${i}`} value={k}>
                                                            {k}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Stack>
                                            <Stack flexDirection='row'>
                                                <IconButton
                                                    size='small'
                                                    onClick={() => handleRemoveAggregations(aggregationIdx)}
                                                    disabled={disable}>
                                                    <DeleteRounded fontSize='small' color={disable ? undefined : 'error'} />
                                                </IconButton>
                                            </Stack>
                                        </Stack>
                                    ))}
                                </Stack>
                            </>
                        )
                    : <Typography variant='caption'>Perform aggregates operations (you may choose to leave operations empty if you want perform only grouping)</Typography>}
                <Button
                    size='small'
                    startIcon={<AddRounded fontSize='small' />}
                    variant='text'
                    onClick={handleAddAggregations}
                    disabled={disable || (selectedNode.data.groupingData?.aggregations && selectedNode.data.groupingData.aggregations.some(x => x.as === '' || (x.op !== 'count' && x.field === '')))}>
                    add operation
                </Button>
            </Stack>
        </Stack>
    )
}

export default memo(NodeAggregateByEditor)
